// The Expertise vocabulary — "what can this member actually do?".
//
// This is the standardized expertise taxonomy agreed for GWBI: 104 entries
// across 15 thematic areas. It replaces the placeholder list this feature
// shipped with, and it is the benchmark — items should be changed here, in one
// place, and never copied into a second file. The wine themes already show what
// happens when a controlled list acquires a shadow copy: see
// dashboard/utils/themesmap.js, where adding a theme in the database renders it
// unlabelled.
//
// Deliberately a different axis from `themes`. A theme says what someone works
// ON (sustainability, wine tourism); an expertise says what they can EXECUTE
// (applied econometrics, hedonic pricing models, climate impact assessment).
// Consortium partners are picked on the second axis, which is why the theme
// filter on GWBI People never answered "who can run the sensory work package?".
//
// IDs are stable slugs derived from the label, and they are what is stored on a
// member. Labels may be reworded freely; an ID must never be reused for a
// different expertise, and a withdrawn one belongs in RETIRED_EXPERTISE_IDS
// rather than deleted, or every member who picked it silently loses the answer.

// How many a member may pick. A cap is what keeps the directory useful: with
// 104 entries and no cap, a member who ticks thirty makes every search return
// them, and the search stops discriminating.
export const MAX_EXPERTISE_SELECTIONS = 8;

// Below this many members with a saved expertise profile, matching leans on the
// rest of the profile (themes, regions, headline, biography). Above it, only
// expertise counts. See expertiseService.pickMatchMode.
export const EXPERTISE_ONLY_THRESHOLD = 100;

export const EXPERTISE_GROUPS = [
  {
    key: "agribusiness",
    label: "Agribusiness, Strategy and Value Chains",
    blurb: "Firms, chains, competitiveness and internationalisation.",
  },
  {
    key: "viticulture",
    label: "Agricultural Production and Viticulture",
    blurb: "The vineyard and the crop, from ecophysiology to field trials.",
  },
  {
    key: "climate",
    label: "Climate, Environment and Natural Resources",
    blurb: "Climate analysis, modelling and resource economics.",
  },
  {
    key: "consumer",
    label: "Consumer, Market and Behavioural Research",
    blurb: "Preference, choice and what people say against what they do.",
  },
  {
    key: "econometrics",
    label: "Econometrics, Statistics and Data Science",
    blurb: "Estimation, inference and quantitative method.",
  },
  {
    key: "food-systems",
    label: "Food Systems and Rural Development",
    blurb: "Food economics, quality and rural change.",
  },
  {
    key: "trade",
    label: "International Economics and Trade",
    blurb: "Trade flows, barriers and exchange rates.",
  },
  {
    key: "policy",
    label: "Policy, Governance and Institutions",
    blurb: "Regulation, public policy and institutional analysis.",
  },
  {
    key: "qualitative",
    label: "Research Design and Qualitative Methods",
    blurb: "Study design, interviews, cases and text.",
  },
  {
    key: "social-history",
    label: "Social Sciences and Economic History",
    blurb: "History, geography, sociology and gender.",
  },
  {
    key: "sustainability",
    label: "Sustainability and Corporate Responsibility",
    blurb: "Circularity, indicators and responsible business.",
  },
  {
    key: "sensory",
    label: "Sensory and Perceptual Science",
    blurb: "Perception, panels and tasting design.",
  },
  {
    key: "tourism",
    label: "Tourism and Territorial Development",
    blurb: "Wine tourism, territory and cultural landscape.",
  },
  {
    key: "wine-business",
    label: "Wine Economics and Business",
    blurb: "Wine markets, prices, firms and assets.",
  },
  {
    key: "oenology",
    label: "Wine Science and Oenology",
    blurb: "The cellar, wine chemistry and production.",
  },
];

// One flat list, ordered by thematic area and then as the taxonomy orders them.
// Rendering order comes from here, so the picker and every profile show the same
// expertise in the same sequence.
export const EXPERTISE_ITEMS = [

  // — Agribusiness, Strategy and Value Chains —
  { id: "agribusiness-competitiveness-cluster-analysis", group: "agribusiness", label: "Agribusiness competitiveness and cluster analysis" },
  { id: "agribusiness-strategy-marketing", group: "agribusiness", label: "Agribusiness strategy and marketing" },
  { id: "agri-food-cost-modelling-benchmarking", group: "agribusiness", label: "Agri-food cost modelling and benchmarking" },
  { id: "business-internationalization-networks", group: "agribusiness", label: "Business internationalization and networks" },
  { id: "family-business", group: "agribusiness", label: "Family business" },
  { id: "global-marketing-strategy", group: "agribusiness", label: "Global marketing strategy" },
  { id: "financial-planning-budgeting", group: "agribusiness", label: "Financial planning and budgeting" },
  { id: "institutional-economics-agribusiness", group: "agribusiness", label: "Institutional economics of agribusiness" },
  { id: "servitization", group: "agribusiness", label: "Servitization" },
  { id: "value-chain-supply-chain-analysis", group: "agribusiness", label: "Value chain and supply chain analysis" },

  // — Agricultural Production and Viticulture —
  { id: "agricultural-technology-adoption", group: "viticulture", label: "Agricultural technology adoption" },
  { id: "crop-modelling-dynamic-simulation", group: "viticulture", label: "Crop modelling and dynamic simulation" },
  { id: "crop-responses-to-environmental-factors", group: "viticulture", label: "Crop responses to environmental factors" },
  { id: "field-experimentation-vineyard-management", group: "viticulture", label: "Field experimentation and vineyard management" },
  { id: "fungus-resistant-grape-varieties", group: "viticulture", label: "Fungus-resistant grape varieties" },
  { id: "plant-ecophysiology-phenology", group: "viticulture", label: "Plant ecophysiology and phenology" },
  { id: "terroir-site-selection", group: "viticulture", label: "Terroir and site selection" },
  { id: "technical-advisory-participatory-work-with-growers", group: "viticulture", label: "Technical advisory and participatory work with growers" },
  { id: "xylem-hydraulics", group: "viticulture", label: "Xylem hydraulics" },

  // — Climate, Environment and Natural Resources —
  { id: "agroclimatic-environmental-spatial-analysis", group: "climate", label: "Agroclimatic and environmental spatial analysis" },
  { id: "atmospheric-deposition-air-quality", group: "climate", label: "Atmospheric deposition and air quality" },
  { id: "climate-change-analysis-scenario-development", group: "climate", label: "Climate change analysis and scenario development" },
  { id: "climate-impact-assessment", group: "climate", label: "Climate impact assessment" },
  { id: "climate-modelling", group: "climate", label: "Climate modelling" },
  { id: "hydrological-modelling-drought-assessment", group: "climate", label: "Hydrological modelling and drought assessment" },
  { id: "meteorology-climate-crop-interactions", group: "climate", label: "Meteorology and climate–crop interactions" },
  { id: "natural-resource-environmental-economics", group: "climate", label: "Natural resource and environmental economics" },
  { id: "remote-sensing-satellite-climate-data-analysis", group: "climate", label: "Remote sensing and satellite climate data analysis" },

  // — Consumer, Market and Behavioural Research —
  { id: "attitude-behaviour-gap-analysis", group: "consumer", label: "Attitude–behaviour gap analysis" },
  { id: "behavioural-economics", group: "consumer", label: "Behavioural economics" },
  { id: "consumer-behaviour", group: "consumer", label: "Consumer behaviour" },
  { id: "consumer-choice-preference-modelling", group: "consumer", label: "Consumer choice and preference modelling" },
  { id: "consumer-market-research", group: "consumer", label: "Consumer and market research" },
  { id: "experimental-economics-choice-experiments", group: "consumer", label: "Experimental economics and choice experiments" },
  { id: "online-purchasing-experiments", group: "consumer", label: "Online purchasing experiments" },
  { id: "stated-preference-valuation-methods", group: "consumer", label: "Stated-preference valuation methods" },
  { id: "willingness-to-pay-analysis", group: "consumer", label: "Willingness-to-pay analysis" },

  // — Econometrics, Statistics and Data Science —
  { id: "applied-econometrics", group: "econometrics", label: "Applied econometrics" },
  { id: "bibliometric-text-analysis", group: "econometrics", label: "Bibliometric and text analysis" },
  { id: "causal-inference-impact-evaluation", group: "econometrics", label: "Causal inference and impact evaluation" },
  { id: "data-collection-management-visualization", group: "econometrics", label: "Data collection, management and visualization" },
  { id: "efficiency-productivity-analysis", group: "econometrics", label: "Efficiency and productivity analysis" },
  { id: "economic-modelling", group: "econometrics", label: "Economic modelling" },
  { id: "heckman-selection-models", group: "econometrics", label: "Heckman selection models" },
  { id: "hedonic-pricing-models", group: "econometrics", label: "Hedonic pricing models" },
  { id: "machine-learning-big-data-analysis", group: "econometrics", label: "Machine learning and big-data analysis" },
  { id: "panel-data-econometrics", group: "econometrics", label: "Panel-data econometrics" },
  { id: "partial-least-squares-modelling", group: "econometrics", label: "Partial least squares modelling" },
  { id: "spatial-econometrics-gis", group: "econometrics", label: "Spatial econometrics and GIS" },
  { id: "statistical-multivariate-analysis", group: "econometrics", label: "Statistical and multivariate analysis" },

  // — Food Systems and Rural Development —
  { id: "food-beverage-economics", group: "food-systems", label: "Food and beverage economics" },
  { id: "food-quality-assessment", group: "food-systems", label: "Food quality assessment" },
  { id: "food-studies-development", group: "food-systems", label: "Food studies and development" },
  { id: "rural-agricultural-development", group: "food-systems", label: "Rural and agricultural development" },
  { id: "sustainable-rural-development", group: "food-systems", label: "Sustainable rural development" },

  // — International Economics and Trade —
  { id: "exchange-rate-analysis", group: "trade", label: "Exchange-rate analysis" },
  { id: "gravity-models-trade", group: "trade", label: "Gravity models of trade" },
  { id: "international-economics", group: "trade", label: "International economics" },
  { id: "international-trade-analysis-theory", group: "trade", label: "International trade analysis and theory" },
  { id: "tariff-non-tariff-measures", group: "trade", label: "Tariff and non-tariff measures" },

  // — Policy, Governance and Institutions —
  { id: "agricultural-policy", group: "policy", label: "Agricultural policy" },
  { id: "governance-institutional-analysis", group: "policy", label: "Governance and institutional analysis" },
  { id: "public-policy-analysis", group: "policy", label: "Public policy analysis" },
  { id: "regulatory-economics", group: "policy", label: "Regulatory economics" },
  { id: "water-economics-policy", group: "policy", label: "Water economics and policy" },

  // — Research Design and Qualitative Methods —
  { id: "content-semantic-analysis", group: "qualitative", label: "Content and semantic analysis" },
  { id: "group-concept-mapping", group: "qualitative", label: "Group concept mapping" },
  { id: "mixed-methods-research-design", group: "qualitative", label: "Mixed-methods research design" },
  { id: "qualitative-research-case-study-methods", group: "qualitative", label: "Qualitative research and case-study methods" },
  { id: "survey-research", group: "qualitative", label: "Survey research" },

  // — Social Sciences and Economic History —
  { id: "comparative-economic-history", group: "social-history", label: "Comparative economic history" },
  { id: "economic-geography", group: "social-history", label: "Economic geography" },
  { id: "family-farming", group: "social-history", label: "Family farming" },
  { id: "gender-studies", group: "social-history", label: "Gender studies" },
  { id: "historical-archival-analysis", group: "social-history", label: "Historical and archival analysis" },
  { id: "rural-sociology", group: "social-history", label: "Rural sociology" },
  { id: "visual-sociology", group: "social-history", label: "Visual sociology" },

  // — Sustainability and Corporate Responsibility —
  { id: "circular-economy", group: "sustainability", label: "Circular economy" },
  { id: "corporate-social-responsibility-in-wine-sector", group: "sustainability", label: "Corporate social responsibility in the wine sector" },
  { id: "sustainability-assessment-indicator-development", group: "sustainability", label: "Sustainability assessment and indicator development" },
  { id: "sustainable-development", group: "sustainability", label: "Sustainable development" },
  { id: "sustainable-wine-consumption", group: "sustainability", label: "Sustainable wine consumption" },

  // — Sensory and Perceptual Science —
  { id: "consumer-sensory-research", group: "sensory", label: "Consumer sensory research" },
  { id: "multisensory-cross-modal-perception", group: "sensory", label: "Multisensory and cross-modal perception" },
  { id: "sensory-psychophysical-methods", group: "sensory", label: "Sensory and psychophysical methods" },
  { id: "sensory-data-analysis", group: "sensory", label: "Sensory data analysis" },
  { id: "wine-tasting-experimental-design", group: "sensory", label: "Wine-tasting experimental design" },

  // — Tourism and Territorial Development —
  { id: "economic-impact-food-wine-tourism", group: "tourism", label: "Economic impact of food, wine and tourism" },
  { id: "heritage-cultural-landscape-studies", group: "tourism", label: "Heritage and cultural landscape studies" },
  { id: "rural-indigenous-tourism", group: "tourism", label: "Rural and Indigenous tourism" },
  { id: "sustainable-tourism-regional-planning", group: "tourism", label: "Sustainable tourism and regional planning" },
  { id: "tourism-economics-valuation", group: "tourism", label: "Tourism economics and valuation" },
  { id: "tourism-research-methods", group: "tourism", label: "Tourism research methods" },
  { id: "tourism-systems-governance", group: "tourism", label: "Tourism systems and governance" },
  { id: "wine-tourism-territorial-development", group: "tourism", label: "Wine tourism and territorial development" },

  // — Wine Economics and Business —
  { id: "fine-wine-as-alternative-asset", group: "wine-business", label: "Fine wine as an alternative asset" },
  { id: "wine-economics", group: "wine-business", label: "Wine economics" },
  { id: "wine-industry-dynamics-firm-performance", group: "wine-business", label: "Wine industry dynamics and firm performance" },
  { id: "wine-market-structure-pricing", group: "wine-business", label: "Wine market structure and pricing" },
  { id: "wine-marketing", group: "wine-business", label: "Wine marketing" },
  { id: "wine-supply-chain-economics-policy", group: "wine-business", label: "Wine supply-chain economics and policy" },

  // — Wine Science and Oenology —
  { id: "low-no-alcohol-wine-production", group: "oenology", label: "Low- and no-alcohol wine production" },
  { id: "wine-chemistry", group: "oenology", label: "Wine chemistry" },
  { id: "winemaking", group: "oenology", label: "Winemaking" },
];

// Expertise withdrawn from the picker but still honoured on profiles that hold
// it. Never delete an id outright — see the header.
//
// The placeholder vocabulary this feature launched with is not listed here: it
// was replaced before any member had saved against it, so there is nothing to
// honour. Anything retired from the taxonomy above does belong here.
export const RETIRED_EXPERTISE_IDS = [];

export const EXPERTISE_BY_ID = EXPERTISE_ITEMS.reduce((acc, item) => {
  acc[item.id] = item;
  return acc;
}, {});

export const EXPERTISE_GROUP_BY_KEY = EXPERTISE_GROUPS.reduce((acc, group) => {
  acc[group.key] = group;
  return acc;
}, {});

export const itemsInGroup = (groupKey) =>
  EXPERTISE_ITEMS.filter((item) => item.group === groupKey);

export const expertiseLabel = (id) => EXPERTISE_BY_ID[id]?.label || "";

// Keeps only ids the vocabulary knows, de-duplicated, in vocabulary order so
// two members who picked the same expertise always render it the same way.
export const sanitizeExpertiseIds = (values) => {
  const wanted = new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => String(value ?? "").trim())
      .filter(Boolean)
  );
  return EXPERTISE_ITEMS.filter((item) => wanted.has(item.id)).map((i) => i.id);
};

// Which accordion panels start open. Fifteen closed groups is a wall, and the
// two broadest ones carry the methods most members here share. Everything else
// is one click away; nothing is hidden.
export const defaultOpenGroupKeys = () => ["econometrics", "consumer"];

// Who sees Expertise at all.
//
// The feature is aimed at people who publish and apply for grants, so it is
// shown to members who declared themselves academics or students and hidden
// from everyone else rather than shown to all and ignored by most. This gates
// display only: nothing here filters search results, so an industry member who
// has filled expertise in is still found by an academic searching for them.
//
// Matched against `type`, the personal role declared at registration. A member
// whose role is recorded as "Other" is not treated as academic even if their
// free-text answer says otherwise — extend this list rather than parsing that.
export const ACADEMIC_MEMBER_TYPES = ["Academic / Researcher", "Student"];

export const isAcademicProfile = (profile) => {
  if (!profile) return false;
  // An account acting as an organisation is not a person with a method.
  if (String(profile.account_kind || "").trim() === "organization") return false;
  return ACADEMIC_MEMBER_TYPES.includes(String(profile.type || "").trim());
};
